// Standard library imports
import React, { useEffect, useState } from 'react';

// External library imports
import moment from 'moment-timezone';
import _ from 'lodash';

// Internal module imports
import Typography from '../../../components/Typography/Typography';
import { timeOnly } from '../../../utils/dateHelper';
import { DownloadAsExcel } from '../../../utils/downloadAsExcel';
import TabsComponent from '../../../components/Tabs/Tabs';
import DatePicker from '../../../components/Inputs/DatePicker/DatePicker';
import DownloadButton from '../../../components/Buttons/DownloadButton';
import { InstanceDataLogsService } from '../../../services/InstanceDataLogsService';
import EChart from '../../../components/Echarts/Echarts';
import { toCET } from '../../../utils/timeHelper';
import { usToEuCurrencyFormat } from '../../../utils/currencyHelper';

// Css imports
import classes from '../../../styles/Overview.module.css';

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(yesterday.getDate() - 1);

const Overview = ({ device, startLoader, stopLoader, updateQueryParams, queryParamsData }) => {
    const [lastStatus, setLastStatus] = useState('--');
    const [date, setDate] = useState(queryParamsData.date ? queryParamsData.date : today);
    const [graphData, setGraphData] = useState([]);
    const [consumptionData, setConsumptionData] = useState([]);
    const [socData, setSocData] = useState([]);
    const [selectedIndex] = useState(queryParamsData.subTab ? queryParamsData.subTab : 0);

    useEffect(() => {
        updateQueryParams('date', date)
    }, [date]);

    useEffect(() => {
        if (device.name !== 'overview' && device._id) {
            let filteredParams = {
                groupId: device._id,
                date: moment(date).tz('Europe/Berlin').format('YYYY-MM-DD'),
            };
            InstanceDataLogsService.Overview(
                filteredParams,
                () => startLoader('getInstanceOverview'),
                handleGetDeviceDataSuccess,
                handleError,
                () => stopLoader('getInstanceOverview')
            );
        }
    }, [device, date]);

    useEffect(() => {
        if (device._id) {
            InstanceDataLogsService.LastReported(
                { groupId: device._id },
                () => startLoader('getOverviewGraphLastReported'),
                handleGetLastReportedSuccess,
                handleError,
                () => stopLoader('getOverviewGraphLastReported')
            );
        }
    }, [device]);

    const handleGetDeviceDataSuccess = ({ data }) => {
        setGraphData(data.data.powerData);
        setConsumptionData(data.data.consumptionData || [{ data: [] }]);
        setSocData(data.data.socData || [{ data: [] }]);
    };
    const handleGetLastReportedSuccess = ({ data }) => {
        setLastStatus(data?.data?.lastReported);
    };

    const handleExport = (type) => {
        const powerData = graphData[0]?.data || [];
        const consumptionDataPoints = consumptionData[0]?.data || [];
        const socDataPoints = socData[0]?.data || [];

        const mergedMap = new Map();

        powerData.forEach((item) => {
            mergedMap.set(toCET(_.get(item, [0])), { date: toCET(_.get(item, [0])), power: _.get(item, [1]) });
        });

        consumptionDataPoints.forEach((item) => {
            const existing = mergedMap.get(toCET(_.get(item, [0]))) || { date: toCET(_.get(item, [0])) };
            existing.consumption = _.get(item, [1]);
            mergedMap.set(toCET(_.get(item, [0])), existing);
        });

        socDataPoints.forEach((item) => {
            const existing = mergedMap.get(toCET(_.get(item, [0]))) || { date: toCET(_.get(item, [0])) };
            existing.soc = _.get(item, [1]);
            mergedMap.set(toCET(_.get(item, [0])), existing);
        });

        const downloadData = Array.from(mergedMap.values());

        const headers = ['Date & Time', 'Power (kW)', 'Consumption (kWh)', 'SoC (%)'];

        if (downloadData.length) {
            DownloadAsExcel(downloadData, `${device?.name} Smart Charging Data ${moment(date).format('DD-MM-YYYY')}`, headers);
        }
    };

    const handleError = (err) => console.log(err);

    const tabs = [
        {
            name: 'Power (kW)',
            component: (
                <div className={classes.ChartContainer}>
                    <div>
                        <div className={classes.Actions}>
                            <DatePicker date={date} setDate={setDate} />
                            <DownloadButton size="meduim" onClick={() => handleExport('power')} />
                        </div>
                    </div>
                    <div style={{ flexGrow: 1 }}>
                        <EChart series={graphData} type="line" xFormatter={timeOnly} yFormatter={(v) => usToEuCurrencyFormat(parseFloat(v).toFixed(2))} yTooltipSuffix={['kW']} />
                    </div>
                </div>
            ),
        },
        {
            name: 'Consumption (kWh)',
            component: (
                <div className={classes.ChartContainer}>
                    <div className={classes.Actions}>
                        <DatePicker date={date} setDate={setDate} />
                        <DownloadButton size="meduim" onClick={() => handleExport('consumption')} />
                    </div>
                    <div style={{ flexGrow: 1 }}>
                        <EChart
                            series={consumptionData}
                            type="line"
                            lineType="area"
                            xFormatter={timeOnly}
                            yFormatter={(v) => usToEuCurrencyFormat(parseFloat(v).toFixed(2))}
                            yTooltipSuffix={['kWh']}
                        />
                    </div>
                </div>
            ),
        },
        {
            name: 'SoC (%)',
            component: (
                <div className={classes.ChartContainer}>
                    <div className={classes.Actions}>
                        <DatePicker date={date} setDate={setDate} />
                        <DownloadButton size="meduim" onClick={() => handleExport('soc')} />
                    </div>
                    <div style={{ flexGrow: 1 }}>
                        <EChart
                            series={socData}
                            type="line"
                            xFormatter={timeOnly}
                            yFormatter={(v) => usToEuCurrencyFormat(parseFloat(v).toFixed(2))}
                            yTooltipSuffix={['%']}
                        />
                    </div>
                </div>
            ),
        },
    ];

    return (
        <div className={classes.Overview}>
            <div className={classes.Header}>
                <div>
                    <Typography content={device.name || '--'} />
                    <div>
                        <Typography subtext content={'Last Reported: ' + (lastStatus || ' --')} />
                    </div>
                </div>
            </div>
            <div className={classes.OverviewBorder}></div>
            <div className={classes.OverviewContent}>
                <TabsComponent
                    tabs={tabs}
                    selectedIndex={selectedIndex}
                    onChangeTab={(index) => updateQueryParams('subTab', index)}
                />
            </div>
        </div>
    );
};

export default Overview;
